import axios from 'axios';

const API_BASE_URL = 'https://api.bizzboy.in/api';
const API_AUTH_URL = 'https://api.bizzboy.in/authenticate';

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' }
});

// Helper function to extract user-friendly error messages from any API error
export const extractErrorMessage = (error: any): string => {
  if (!error) return 'An unexpected error occurred.';
  if (typeof error === 'string') return cleanErrorMessage(error);

  const response = error.response;
  if (response) {
    const data = response.data;

    // 1. If response.data is a string directly
    if (typeof data === 'string' && data.trim()) {
      return cleanErrorMessage(data.trim());
    }

    // 2. If response.data is an object with standard fields
    if (data && typeof data === 'object') {
      if (typeof data.message === 'string' && data.message.trim()) {
        return cleanErrorMessage(data.message.trim());
      }
      if (typeof data.error === 'string' && data.error.trim()) {
        return cleanErrorMessage(data.error.trim());
      }
      if (typeof data.detail === 'string' && data.detail.trim()) {
        return cleanErrorMessage(data.detail.trim());
      }
      if (typeof data.details === 'string' && data.details.trim()) {
        return cleanErrorMessage(data.details.trim());
      }
      if (typeof data.msg === 'string' && data.msg.trim()) {
        return cleanErrorMessage(data.msg.trim());
      }
      if (typeof data.errorMessage === 'string' && data.errorMessage.trim()) {
        return cleanErrorMessage(data.errorMessage.trim());
      }
      if (typeof data.description === 'string' && data.description.trim()) {
        return cleanErrorMessage(data.description.trim());
      }

      // 3. If response.data has an array of validation errors
      if (Array.isArray(data.errors) && data.errors.length > 0) {
        const first = data.errors[0];
        if (typeof first === 'string' && first.trim()) return cleanErrorMessage(first.trim());
        if (first && typeof first.defaultMessage === 'string' && first.defaultMessage.trim()) {
          return cleanErrorMessage(first.defaultMessage.trim());
        }
        if (first && typeof first.message === 'string' && first.message.trim()) {
          return cleanErrorMessage(first.message.trim());
        }
      }

      // 4. If response.data.errors is a field map { phone: "already exists" }
      if (data.errors && typeof data.errors === 'object') {
        const vals = Object.values(data.errors);
        if (vals.length > 0) {
          const first = vals[0];
          if (typeof first === 'string' && first.trim()) return cleanErrorMessage(first.trim());
          if (Array.isArray(first) && first.length > 0 && typeof first[0] === 'string') {
            return cleanErrorMessage(first[0].trim());
          }
        }
      }
    }

    // Fallbacks based on HTTP status codes
    switch (response.status) {
      case 400:
        return 'Bad request. Please check the data provided.';
      case 401:
        return 'Unauthorized. Please log in again.';
      case 403:
        return 'Access denied or session expired. Please log in again.';
      case 404:
        return 'Requested resource not found.';
      case 409:
        return 'Conflict. A record with this information already exists.';
      case 422:
        return 'Unprocessable entity. Please verify your input.';
      case 500:
        return 'Internal server error. Please try again later.';
      default:
        return 'Server error. Please try again later.';
    }
  }

  // Network / client-side errors
  if (error.message && typeof error.message === 'string') {
    if (error.message === 'Network Error') {
      return 'Network error. Please check your internet connection.';
    }
    return cleanErrorMessage(error.message);
  }

  return 'An unexpected error occurred. Please try again.';
};

// Clean technical database/SQL constraint messages into clear user messages
const cleanErrorMessage = (msg: string): string => {
  if (!msg) return '';
  const lower = msg.toLowerCase();

  // Duplicate phone/mobile number constraint
  if (
    (lower.includes('duplicate') || lower.includes('unique') || lower.includes('constraint') || lower.includes('already exist')) &&
    (lower.includes('phone') || lower.includes('mobile') || lower.includes('phone_number') || lower.includes('mobile_number') || lower.includes('users_phone') || lower.includes('users_mobile'))
  ) {
    return 'Phone number already exists. Please use a different phone number.';
  }

  // Duplicate username
  if (
    (lower.includes('duplicate') || lower.includes('unique') || lower.includes('constraint')) &&
    (lower.includes('username') || lower.includes('user_name'))
  ) {
    return 'Username already exists. Please choose a different username.';
  }

  // Duplicate email
  if (
    (lower.includes('duplicate') || lower.includes('unique') || lower.includes('constraint')) &&
    lower.includes('email')
  ) {
    return 'Email address already exists. Please use a different email.';
  }

  return msg;
};

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    if (config.url?.endsWith("authenticate")) {
      config.url = API_AUTH_URL;
    } else {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const { response } = error;

    if (response) {
      if (response.status === 401) {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      }
    }

    const errorMessage = extractErrorMessage(error);
    const customError = new Error(errorMessage);
    (customError as any).response = response;
    (customError as any).status = response?.status;
    (customError as any).data = response?.data;

    return Promise.reject(customError);
  }
);

export const postData = async (path: string, data: any) => {
  try {
    const response = await axiosInstance.post(path.startsWith('http') ? path : `${API_BASE_URL}/${path}`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const putData = async (path: string, data: any) => {
  try {
    const response = await axiosInstance.put(path.startsWith('http') ? path : `${API_BASE_URL}/${path}`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetchData = async (path: string, params: any = {}) => {
  try {
    const response = await axiosInstance.get(path.startsWith('http') ? path : `${API_BASE_URL}/${path}`, { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export default axiosInstance;
